import type { CharacterStyle } from '../core/characters';
import { FIGURE, type FigureBuild } from './figure';
import { characterDetails, characterSkin, type StyleParts } from './characterStyle';
import { group, Layer, type ShapeGroup } from './frame';
import { inkOutline } from './staging';
import { shade, type Primitive } from './sdf/primitives';

export const CHARACTER_INK = '#151d29';
export const CHARACTER_OUTLINE = 0.042;
const LIMB_OUTLINE = 0.032;

function skinGroups(panel: { panel: Primitive[]; trace: Primitive[] }, color: string, layer: number, glow = true): ShapeGroup[] {
  return [
    group(panel.panel, CHARACTER_INK, layer, { style: 'flat', pad: 0.1 }),
    group(panel.trace, color, layer, { style: 'flat', fx: glow ? 'glow' : 'none', glow: 0.055, pad: 0.1 }),
    group(panel.trace.map((p) => ({ ...p, r: p.r * 0.43 })), shade(color, 0.7), layer, { style: 'flat', pad: 0.1 }),
  ];
}

/** Ragdoll details follow their joints and lose the live fighter's emissive halo. */
export function ragdollSkinGroups(parts: StyleParts, color: string, style: CharacterStyle): ShapeGroup[] {
  const skin = characterSkin(parts, style);
  return skin ? [skin.torso, skin.main, skin.off].flatMap((panel) => skinGroups(panel, shade(color, -0.08), Layer.Ragdolls, false)) : [];
}

/** Leave the attachment to the shoulder/hip open, rather than drawing a ring around the joint. */
function limbOutline(primitives: Primitive[]): Primitive[] {
  return primitives.map((p, i) => {
    if (i !== 0) return p;
    const dx = p.bx - p.ax, dy = p.by - p.ay, length = Math.hypot(dx, dy);
    if (length < 0.001) return p;
    const trim = Math.min(length * 0.9, Math.sqrt((p.r + LIMB_OUTLINE) ** 2 - p.r ** 2) + 0.015);
    return { ...p, ax: p.ax + dx * trim / length, ay: p.ay + dy * trim / length };
  });
}

/** Shared painter order for the arena and SVG previews: near limbs sit above the torso. */
export function characterGroups(fig: FigureBuild, color: string, style: CharacterStyle, layer = Layer.Players): ShapeGroup[] {
  const fill = (primitives: Primitive[]) => group(primitives, color, layer, { blend: 'smoothUnion', smoothK: FIGURE.smoothK, style: 'shaded', pad: 0.4 });
  const details = characterDetails({ ...fig, ...fig.joints }, style);
  const skin = characterSkin({ ...fig, ...fig.joints }, style);
  const groups: ShapeGroup[] = [];
  if (details.back.length) groups.push(inkOutline(details.back, 0.035, layer, CHARACTER_INK), group(details.back, '#fff1cf', layer, { style: 'flat' }));
  groups.push(inkOutline(fig.body, CHARACTER_OUTLINE, layer, CHARACTER_INK), fill(fig.body));
  if (skin) groups.push(...skinGroups(skin.torso, color, layer));
  // Face and body markings belong behind an arm crossing the chest or face.
  groups.push(group(fig.eyes, '#1b1c22', layer, { style: 'flat' }));
  const handWear = style.accessory === 'wraps' || style.accessory === 'gloves';
  if (details.wear.length && !handWear) groups.push(group(details.wear, '#fff1cf', layer, { style: 'flat' }));
  if (details.dark.length) groups.push(group(details.dark, '#1b1c22', layer, { style: 'flat' }));
  if (details.markings.length) {
    // A fine keyline keeps cream markings legible on yellow and separates them from neon traces.
    groups.push(inkOutline(details.markings, 0.012, layer, CHARACTER_INK), group(details.markings, '#fff1cf', layer, { style: 'flat' }));
  }
  const wear = (primitives: Primitive[]) => {
    if (!primitives.length) return;
    if (style.accessory === 'gloves') groups.push(inkOutline(primitives, 0.035, layer, CHARACTER_INK));
    groups.push(group(primitives, '#fff1cf', layer, { style: 'flat' }));
  };
  if (skin) groups.push(...skinGroups(skin.off, color, layer));
  wear(details.offWear);
  if (fig.handInk.off.length) groups.push(group(fig.handInk.off, CHARACTER_INK, layer, { style: 'flat' }));
  for (const limb of [fig.nearLeg, fig.nearArm]) {
    groups.push(inkOutline(limbOutline(limb), LIMB_OUTLINE, layer, CHARACTER_INK), fill(limb));
  }
  if (skin) groups.push(...skinGroups(skin.main, color, layer));
  wear(details.mainWear);
  if (fig.handInk.main.length) groups.push(group(fig.handInk.main, CHARACTER_INK, layer, { style: 'flat' }));
  return groups;
}

/** Fingers wrap over the weapon while the palm and wrist remain behind its handle. */
export function grippingHandGroups(fig: FigureBuild, color: string, style: CharacterStyle): ShapeGroup[] {
  if (!fig.grip.fingers.length) return [];
  const layer = Layer.Players + 0.55;
  return [
    inkOutline(fig.grip.fingers, 0.022, layer, CHARACTER_INK),
    group(fig.grip.fingers, style.accessory === 'gloves' ? '#fff1cf' : color, layer, { blend: 'smoothUnion', smoothK: 0.025, style: 'shaded' }),
    group(fig.grip.creases, CHARACTER_INK, layer, { style: 'flat' }),
  ];
}
